import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { authAPI } from "@/services/api";
import {
  isFirebaseConfigured,
  isFirebaseProjectCompatible,
  getPendingGoogleCredential,
  linkGoogleToEmailAccount,
  signInWithGoogle,
} from "@/lib/firebase";

/**
 * Google OAuth + session exchange for client or BRM.
 */
const FirebaseAuthPanel = ({
  role,
  referralCode = null,
  clientName = null,
  onSession,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [firebaseEnabled, setFirebaseEnabled] = useState(false);
  const [pendingLink, setPendingLink] = useState(null);
  const [linkPassword, setLinkPassword] = useState("");

  useEffect(() => {
    authAPI
      .getConfig()
      .then((cfg) => setFirebaseEnabled(
        cfg.enabled && isFirebaseConfigured() && isFirebaseProjectCompatible(cfg.project_id),
      ))
      .catch(() => setFirebaseEnabled(false));
  }, []);

  const exchangeToken = async (idToken) => {
    const session = await authAPI.firebaseSession({
      id_token: idToken,
      role,
      name: clientName || undefined,
      referral_code: referralCode || undefined,
    });
    onSession(session);
    return session;
  };

  const handleGoogle = async () => {
    if (!firebaseEnabled) {
      toast.error("Firebase is not configured for this environment");
      return;
    }
    setLoading(true);
    try {
      const idToken = await signInWithGoogle();
      const session = await exchangeToken(idToken);
      toast.success(session.is_new_user ? "Welcome to Proovam!" : "Signed in successfully");
    } catch (error) {
      const code = error?.code;
      if (code === "auth/popup-closed-by-user") {
        return;
      }
      if (code === "auth/account-exists-with-different-credential") {
        const credential = getPendingGoogleCredential(error);
        const email = error?.customData?.email || error?.email;
        if (credential && email) {
          setPendingLink({ credential, email });
          toast.info("Sign in once with your existing password to link Google safely.");
          return;
        }
      }
      const googleErrorMessages = {
        "auth/operation-not-allowed": "Google sign-in is not enabled for Proovam yet. Please use email and password or contact support.",
        "auth/unauthorized-domain": "This address is not approved for Google sign-in. Open Proovam from its official website and try again.",
        "auth/popup-blocked": "Your browser blocked the Google sign-in window. Allow pop-ups for Proovam and try again.",
        "auth/cancelled-popup-request": "Another Google sign-in window is already open.",
        "auth/invalid-api-key": "Google sign-in is misconfigured for this release. Please contact support.",
      };
      toast.error(
        googleErrorMessages[code] || error.response?.data?.detail || error.message || "Google sign-in failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (event) => {
    event.preventDefault();
    if (!pendingLink || !linkPassword) {
      toast.error("Enter your existing account password");
      return;
    }
    setLoading(true);
    try {
      const idToken = await linkGoogleToEmailAccount(
        pendingLink.email,
        linkPassword,
        pendingLink.credential,
      );
      const session = await exchangeToken(idToken);
      setPendingLink(null);
      setLinkPassword("");
      toast.success(session.is_new_user ? "Welcome to Proovam!" : "Google sign-in linked successfully");
    } catch (error) {
      toast.error(error.message || "Unable to link Google sign-in");
    } finally {
      setLoading(false);
    }
  };

  if (!firebaseEnabled) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or continue with</span>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full h-11 border-gray-200"
        onClick={handleGoogle}
        disabled={loading || disabled}
        data-testid={`firebase-google-${role}`}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        )}
        Continue with Google
      </Button>
      {pendingLink && (
        <form onSubmit={handleLink} className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            <span className="font-semibold">{pendingLink.email}</span> already has a sign-in method.
            Enter its password once to link Google; no second Proovam account will be created.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor={`google-link-password-${role}`}>Existing account password</Label>
            <Input
              id={`google-link-password-${role}`}
              type="password"
              autoComplete="current-password"
              value={linkPassword}
              onChange={(event) => setLinkPassword(event.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={loading || !linkPassword}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Link Google and continue
            </Button>
            <Button type="button" variant="outline" onClick={() => { setPendingLink(null); setLinkPassword(""); }} disabled={loading}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FirebaseAuthPanel;
